/**
 * The answer of GET api/v1/OutlookPlugin/Session, kept for an hour.
 *
 * In localStorage, as an unpinned taskpane is loaded anew for every opened mail: asking every time would cost
 * one request per mail for every customer. An hour bounds how long a right granted since stays hidden; a right
 * taken away since only meets the backend's 403. A sign in, a sign out and a company switch ask again right
 * away - see forget.
 */
export default class SessionCache {
    private static readonly STORAGE_KEY = "orphy-outlook-session";
    private static readonly MAX_AGE_MS = 60 * 60 * 1000;

    /** The kept answer, or null once it is older than an hour - or from the future, after the clock was set back. */
    public static read = (): any => {
        try {
            const entry = JSON.parse(localStorage.getItem(SessionCache.STORAGE_KEY));
            const age = Date.now() - entry?.storedAt;
            return age >= 0 && age < SessionCache.MAX_AGE_MS ? entry.session : null;
        } catch (e) {
            return null;
        }
    };

    public static write = (session: any): void => {
        try {
            localStorage.setItem(SessionCache.STORAGE_KEY, JSON.stringify({ storedAt: Date.now(), session: session }));
        } catch (e) {
            // Full or blocked storage only means the session is asked for again next time.
        }
    };

    /** Asks again on the next load. Belongs wherever the answer may differ now: a sign in, a sign out, a company switch. */
    public static forget = (): void => {
        try {
            localStorage.removeItem(SessionCache.STORAGE_KEY);
        } catch (e) {
            // Storage that cannot be reached holds nothing to forget.
        }
    };
}
